/**
 * ExecutionHeader — Ultra-slim single-row cockpit header.
 * Back | StageName + Status | KPIs | Responsible | Actions
 */
import React from 'react';
import { AlertTriangle, ArrowLeft, Clock, Gauge, ListOrdered, Pause, TriangleAlert, User, Users } from 'lucide-react';

interface ExecutionHeaderProps {
  stageName: string;
  executionCode?: string;
  status: 'executing' | 'waiting' | 'paused' | 'completed';
  slaLabel?: string;
  slaStatus?: 'ok' | 'warning' | 'violated' | 'none';
  tpLabel?: string;
  traLabel?: string;
  traPercent?: number | null;
  idlePercent?: number | null;
  responsible?: string;
  priority?: string | null;
  wipCurrent?: number;
  wipLimit?: number;
  queueCount?: number;
  riskLabel?: string;
  riskOk?: boolean;
  onBack: () => void;
  onPause?: () => void;
  onReportProblem?: () => void;
  isPausing?: boolean;
  actions?: React.ReactNode;
}

const STATUS_CONFIG = {
  executing: { dot: 'bg-emerald-500',            tone: 'text-emerald-600 dark:text-emerald-400' },
  waiting:   { dot: 'bg-blue-400',               tone: 'text-blue-600 dark:text-blue-400' },
  paused:    { dot: 'bg-amber-500 animate-pulse', tone: 'text-amber-600 dark:text-amber-400' },
  completed: { dot: 'bg-slate-400',              tone: 'text-slate-500 dark:text-slate-400' },
};

const PRIORITY_STYLES: Record<string, string> = {
  critica: 'text-rose-500',
  alta:    'text-amber-500',
  media:   'text-slate-500',
  baixa:   'text-sky-500',
};

/** Return 1-2 char initials from a name */
function initials(name: string): string {
  const parts = name.trim().split(/\s+/);
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
}

export const ExecutionHeader: React.FC<ExecutionHeaderProps> = ({
  stageName,
  executionCode,
  status,
  priority,
  slaLabel,
  slaStatus,
  tpLabel,
  idlePercent,
  wipCurrent,
  wipLimit,
  queueCount,
  riskLabel,
  riskOk = true,
  responsible,
  onBack,
  onPause,
  onReportProblem,
  isPausing,
  actions,
}) => {
  const cfg    = STATUS_CONFIG[status];
  const priKey = (priority ?? '').toLowerCase();
  const priStyle = PRIORITY_STYLES[priKey] ?? 'text-slate-400';

  return (
    <div className="flex items-center gap-0 border-b border-slate-200 dark:border-[var(--border-default)] bg-white dark:bg-[var(--bg-elevated)] px-2 pr-4 h-16 shrink-0">

      {/* ── Back ──────────────────────────────────────────────── */}
      <button
        type="button"
        onClick={onBack}
        className="flex h-7 shrink-0 items-center gap-1 rounded-sm px-2 text-[11px] font-medium text-slate-400 dark:text-[var(--text-muted)] transition hover:bg-slate-100 dark:hover:bg-[var(--bg-surface)] hover:text-slate-700"
      >
        <ArrowLeft size={12} />
        <span>Fila</span>
      </button>

      <div className="mx-2 h-4 w-px bg-slate-200 dark:bg-[var(--border-default)] shrink-0" />

      {/* ── Stage name + status ────────────────────────────────── */}
      <div className="flex items-center gap-2 min-w-0 shrink-0 max-w-[280px]">
        <div className={`h-[7px] w-[7px] rounded-full shrink-0 ${cfg.dot}`} />
        <h1 className="text-[13px] font-bold text-slate-900 dark:text-[var(--text-primary)] truncate">{stageName}</h1>
        {executionCode && (
          <span className="shrink-0 mt-[3px] font-mono text-[9px] text-slate-400 dark:text-[var(--text-muted)]">{executionCode}</span>
        )}
        {priority && (
          <span className={`shrink-0 mt-[3px] font-mono text-[9px] font-bold uppercase ${priStyle}`}>
            {priority.charAt(0).toUpperCase()}
          </span>
        )}
      </div>

      <div className="mx-3 h-4 w-px bg-slate-200 dark:bg-[var(--border-default)] shrink-0" />

      {/* ── KPIs (icon + value only) ───────────────────────────── */}
      <div className="flex items-center gap-5 flex-1 min-w-0 overflow-hidden">
        {slaLabel && (
          <div className="flex items-center gap-1 shrink-0" title="SLA — prazo de atendimento">
            <AlertTriangle size={10} className={`${
              slaStatus === 'violated' ? 'text-rose-500' :
              slaStatus === 'warning'  ? 'text-amber-500' : 'text-slate-400'
            }`} />
            <span className={`text-[11px] font-semibold tabular-nums ${
              slaStatus === 'violated' ? 'text-rose-600 dark:text-rose-400' :
              slaStatus === 'warning'  ? 'text-amber-600 dark:text-amber-400' :
              'text-slate-600 dark:text-[var(--text-secondary)]'
            }`}>{slaLabel}</span>
          </div>
        )}
        {tpLabel && (
          <div className="flex items-center gap-1 shrink-0" title="Tempo em processo — tempo total desde o início">
            <Clock size={10} className="text-slate-400" />
            <span className="text-[11px] font-semibold tabular-nums text-slate-600 dark:text-[var(--text-secondary)]">{tpLabel}</span>
          </div>
        )}
        {idlePercent != null && (
          <div className="flex items-center gap-1 shrink-0" title="Tempo ocioso — percentual do tempo sem atividade">
            <Gauge size={10} className={`${idlePercent > 50 ? 'text-rose-500' : idlePercent > 25 ? 'text-amber-500' : 'text-slate-400'}`} />
            <span className={`text-[11px] font-semibold tabular-nums ${
              idlePercent > 50 ? 'text-rose-600 dark:text-rose-400' :
              idlePercent > 25 ? 'text-amber-600 dark:text-amber-400' :
              'text-emerald-600 dark:text-emerald-400'
            }`}>{Math.round(idlePercent)}%</span>
          </div>
        )}
        {wipLimit != null && (
          <div className="flex items-center gap-1 shrink-0" title="WIP — itens em progresso / limite da etapa">
            <ListOrdered size={10} className="text-slate-400" />
            <span className={`text-[11px] font-semibold tabular-nums ${
              wipCurrent != null && wipCurrent >= wipLimit ? 'text-rose-600 dark:text-rose-400' : 'text-slate-600 dark:text-[var(--text-secondary)]'
            }`}>{wipCurrent ?? 0}/{wipLimit}</span>
          </div>
        )}
        {queueCount != null && (
          <div className="flex items-center gap-1 shrink-0" title="Fila — itens aguardando nesta etapa">
            <Users size={10} className="text-slate-400" />
            <span className="text-[11px] font-semibold tabular-nums text-slate-600 dark:text-[var(--text-secondary)]">{queueCount}</span>
          </div>
        )}
        {riskLabel && (
          <div className="flex items-center gap-1 shrink-0" title={riskOk ? 'Risco — dentro do limite' : 'Risco — atenção necessária'}>
            <div className={`h-[6px] w-[6px] rounded-full shrink-0 ${riskOk ? 'bg-emerald-500' : 'bg-rose-500'}`} />
            <span className={`text-[11px] font-semibold ${riskOk ? 'text-emerald-600 dark:text-emerald-400' : 'text-rose-600 dark:text-rose-400'}`}>{riskLabel}</span>
          </div>
        )}
      </div>

      {/* ── Responsible ───────────────────────────────────────── */}
      <div className="mx-3 h-4 w-px bg-slate-200 dark:bg-[var(--border-default)] shrink-0" />
      <div className="flex items-center gap-1.5 shrink-0 max-w-[140px]">
        {responsible ? (
          <>
            <div className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-blue-100 dark:bg-blue-900/40">
              <span className="text-[9px] font-bold text-blue-700 dark:text-blue-300">{initials(responsible)}</span>
            </div>
            <span className="text-[11px] font-medium text-slate-600 dark:text-[var(--text-secondary)] truncate">{responsible}</span>
          </>
        ) : (
          <>
            <div className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-slate-100 dark:bg-[var(--bg-surface)]">
              <User size={11} className="text-slate-400" />
            </div>
            <span className="text-[10px] italic text-slate-400">Aguardando</span>
          </>
        )}
      </div>

      {/* ── Action buttons ────────────────────────────────────── */}
      <div className="ml-2 flex items-center gap-1.5 shrink-0">
        {status === 'executing' && onPause && (
          <button
            type="button"
            onClick={onPause}
            disabled={isPausing}
            className="flex h-7 items-center gap-1 rounded-sm border border-slate-200 dark:border-[var(--border-default)] bg-white dark:bg-[var(--bg-surface)] px-2 text-[11px] font-semibold text-slate-600 dark:text-[var(--text-secondary)] transition hover:bg-amber-50 hover:border-amber-300 hover:text-amber-700 dark:hover:bg-amber-500/10 disabled:opacity-50"
          >
            <Pause size={11} />
            Pausar
          </button>
        )}
        {onReportProblem && status !== 'completed' && (
          <button
            type="button"
            onClick={onReportProblem}
            className="flex h-7 items-center gap-1 rounded-sm border border-slate-200 dark:border-[var(--border-default)] bg-white dark:bg-[var(--bg-surface)] px-2 text-[11px] font-semibold text-slate-600 dark:text-[var(--text-secondary)] transition hover:bg-rose-50 hover:border-rose-300 hover:text-rose-700 dark:hover:bg-rose-500/10"
          >
            <TriangleAlert size={11} />
            Problema
          </button>
        )}
        {actions}
      </div>

    </div>
  );
};
